import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query'
import {api} from '@/lib/api'
import type {components} from '@/lib/api-schema'

export type ExamenOcupacional = components['schemas']['ExamenOcupacionalRead']
export type ExamenesPagina = components['schemas']['Page_ExamenOcupacionalRead_']

export const EXAMENES_PAGE_SIZE = 20
export type ExamenTipo = components['schemas']['ExamenTipo']
export type ConceptoAptitud = components['schemas']['ConceptoAptitud']
export type ExamenPatch = components['schemas']['ExamenPatch']

export function useEmployeeExamenes(businessId: string | undefined, employeeId: string | null, page = 1) {
  return useQuery({
    queryKey: ['examenes', businessId, employeeId, page],
    queryFn: async () =>
      (
        await api.get<ExamenesPagina>(`/businesses/${businessId}/employees/${employeeId}/examenes`, {
          params: {page, page_size: EXAMENES_PAGE_SIZE},
        })
      ).data,
    enabled: !!businessId && !!employeeId,
    placeholderData: (previous) => previous,
  })
}

/** La respuesta es siempre la PRIMERA página: se guarda bajo esa clave y se invalidan las demás. */
function examenesTocados(
  queryClient: ReturnType<typeof useQueryClient>,
  businessId: string,
  employeeId: string,
  data: ExamenesPagina
) {
  queryClient.setQueryData(['examenes', businessId, employeeId, 1], data)
  queryClient.invalidateQueries({
    queryKey: ['examenes', businessId, employeeId],
    predicate: (query) => query.queryKey[3] !== 1,
  })
  // El soporte (cuando lo hay) también aterriza como evidencia 3.1.4 ordinaria.
  queryClient.invalidateQueries({queryKey: ['evidence', businessId]})
}

// `veredicto_textual_certificado` es la única puerta de un «no apto» (E1-H06).
export type RegistrarExamenInput = components['schemas']['ExamenRegistrar']
export type DecisionNorma = components['schemas']['DecisionNorma']

export function useRegistrarExamen(businessId: string, employeeId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: RegistrarExamenInput) =>
      (await api.post<ExamenesPagina>(`/businesses/${businessId}/employees/${employeeId}/examenes`, input)).data,
    onSuccess: (data) => {
      examenesTocados(queryClient, businessId, employeeId, data)
    },
  })
}

// Con soporte, el backend lo mueve a la casilla del tipo corregido y recalcula su vencimiento.
export function useCorregirExamen(businessId: string, employeeId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: {examenId: string; patch: ExamenPatch}) =>
      (
        await api.patch<ExamenesPagina>(
          `/businesses/${businessId}/employees/${employeeId}/examenes/${input.examenId}`,
          input.patch
        )
      ).data,
    onSuccess: (data) => {
      examenesTocados(queryClient, businessId, employeeId, data)
    },
  })
}

// Sin `useEliminarExamen` a propósito (PQ-7c): un examen registrado se anula, nunca se borra.
